import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ClientEdit from '../components/client-edit-view';
import {
  hasIllegalCharacters,
  isValidURI,
  isValidEmail
} from 'core/input-validations';
import { uiShowToastrMessage } from 'modules/ui-module';
import { meLocale, meUserName } from 'modules/me-module';
import {
  getClientById,
  portalClientRequestPatch,
  portalClientRequestPost,
  getNewClientCredentials,
  portalClientRequestDelete,
  clientsIsLoading,
  clientAdminRequestGet,
} from 'modules/portal-module';
import { setHandledErrorCodes, removeHandledErrorCodes } from 'lib/error-message-handling';
import { isEqual, cloneDeep } from 'lodash';
import {
  copyTextToClipboard,
  stopEvent,
  getAPIError,
  getClientData,
  findScrollParent
} from 'lib/utility-functions';
import appconfig from 'config/appconfig';
import { uiShowConfirmDialog } from 'modules/ui-module';
import moment from 'moment';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { oauthPortalPermissionsGet, orgPortalPermissionWrite, oauthPortalScopesGet, currentClientOwners, oauthPortalClientOwnerGet, oauthPortalClientOwnerAdd, oauthPortalClientOwnerDelete, oauthPortalScopeIsValid } from "modules/oauthportal-module";
import { roleManagementRolesRequestGet } from "modules/role-management-module";

const sectionKeys = [
  "Details",
  "Ownership",
  "Tokens",
  "Theme",
  "Authorization",
  "Scopes",
  "Authorities",
  "Grant_types",
  "Secrets"
];

const getErrorCodesArray = () => {
  return [[
      'duplicate.name',
      'user.not.found',
    ]
  ];
};

const HEADER_HEIGHT = 30;

export const ClientEditContainer = () => {
  const location = useLocation();
  const params = useParams();
  const history = useNavigate();

  const getValuesFromStore = (state) => {
    let client = {};
    let isEditMode = true;
    const { clientId } = params || '';
    if (clientId) {
      client = getClientById(state, clientId);
    }
    if (isEqual(client, {})) {
      isEditMode = false;
      client = {
        name: '',
        redirectUris: [],
        description: '',
        scopes: [],
        grants: []
      };
    }
    return {
      clientId,
      client,
      meLocale: meLocale(state),
      userEmail: meUserName(state) || "",
      isEditMode,
      newClientCredentials: getNewClientCredentials(state),
      isLoading: clientsIsLoading(state),
      canWrite: !isEditMode ? true : orgPortalPermissionWrite(state),
      owners: isEditMode ? currentClientOwners(state) : []
    };
  };
  const dispatch = useDispatch();
  const externalProps = useSelector(getValuesFromStore);

  const [client, setClient] = useState({
    ...externalProps.client,
    owners: externalProps.owners || []
  });
  const [isEditMode, setEditMode] = useState(externalProps.isEditMode);
  const [errors, setErrors] = useState({});
  const [addRedirectURI, setAddRedirectURI] = useState('');
  const [addOwnerEmail, setAddOwnerEmail] = useState('');
  const [hasPendingOwnerAddition, setHasPendingOwnerAddition] = useState(false);
  const [hasPendingOwnerDeletion, setHasPendingOwnerDeletion] = useState(false);
  const [ownerToDelete, setOwnerToDelete] = useState(null);
  const [detailsContentsClass, setDetailsContentsClass] = useState('');
  const [permissionsContentsClass, setPermissionsContentsClass] = useState('hidden');
  const [credentialsContentsClass, setCredentialsContentsClass] = useState('hidden');
  const [detailsButtonClass, setDetailsButtonClass] = useState('active');
  const [permissionsButtonClass, setPermissionsButtonClass] = useState('');
  const [credentialsButtonClass, setCredentialsButtonClass] = useState('');
  const [permissionsList, setPermissionsList] = useState({});
  const [existingPermissionsNotControlledByDevPortal, setExistingPermissionsNotControlledByDevPortal] = useState([]);
  const [selectedPermissionsControlledByDevPortal, setSelectedPermissionsControlledByDevPortal] = useState([]);
  const [secretCopied, setSecretCopied] = useState(false);
  const [previousUrlPath, setPreviousUrlPath] = useState('');
  const [sideNavTop, setSideNavTop] = useState(200);
  const [openDetailsEditModel, setOpenDetailsEditModel] = useState(false);
  const [openPermissionsEditModel, setPermissionsEditModel] = useState(false);
  const [openOwnershipEditModel, setOpenOwnershipEditModel] = useState(false);
  const [openRolesEditModel, setOpenRolesEditModel] = useState(false);
  const [openImplicitScopesEditModel, setOpenImplicitScopesEditModel] = useState(false);
  const [openRequiredScopesEditModel, setOpenRequiredScopesEditModel] = useState(false);
  const [openScopesEditModel, setOpenScopesEditModel] = useState(false);
  const [chipPermissions, setChipPermissions] = useState({});
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [activeSection, setActiveSection] = useState(sectionKeys[0]);
  const [rawPermissionList, setRawPermissionList] = useState([]);
  const [fullScopesList, setFullScopesList] = useState([]);
  const [fullRolesList, setFullRolesList] = useState([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [userType, setUserType] = useState({
    isOwnedAccount: false,
    isAdmin: false,
    isInternalDev: false,
    isExternalDev: true
  });
  const sideNavRef = React.useRef();

  // Generic handlers for array operations
  const handleArrayAdd = (fieldName) => async (newItem) => {
    if (['scopes', 'implicitScopes', 'requiredScopes', 'roles'].includes(fieldName)) {
      if (!(await validateUserInput(`${fieldName}Input`, newItem, true))) {
        return false;
      }
    }

    const foundClient = { ...client };

    if (!foundClient[fieldName]) {
      foundClient[fieldName] = [];
    }

    if (!foundClient[fieldName].includes(newItem)) {
      foundClient[fieldName] = [...foundClient[fieldName], newItem];
      setClient(foundClient);
    }

    return true;
  };

  const handleArrayDelete = (fieldName) => (itemToDelete) => {
    const foundClient = { ...client };
    if(errors){
      setErrors({});
    }
    if (foundClient[fieldName]) {
      foundClient[fieldName] = foundClient[fieldName].filter(item => item !== itemToDelete);
      setClient(foundClient);
    }

    return true;
  };

  const handleBooleanChange = (fieldName) => (e) => {
    const isChecked = e.target && e.target.checked;
    const foundClient = { ...client };

    foundClient[fieldName] = isChecked;
    setClient(foundClient);
    return true;
  };

  // Specific handlers using generic functions
  const handleAuthorizationChange = handleBooleanChange;
  const handleAddRole = handleArrayAdd('roles');
  const handleDeleteRole = handleArrayDelete('roles');
  const handleAddImplicitScope = handleArrayAdd('implicitScopes');
  const handleDeleteImplicitScope = handleArrayDelete('implicitScopes');
  const handleAddRequiredScope = handleArrayAdd('requiredScopes');
  const handleDeleteRequiredScope = handleArrayDelete('requiredScopes');
  const handleAddScope = handleArrayAdd('scopes');
  const handleDeleteScope = handleArrayDelete('scopes');

  const origState = React.useRef();
  const storeOrigStateNow = React.useRef(false);

  const sectionRefs = useRef(
    Object.fromEntries(sectionKeys.map(key => [key, React.createRef()]))
  );

  useEffect(() => {
    if (isEditMode) {
      dispatch(oauthPortalPermissionsGet(externalProps.client.clientId));
      dispatch(oauthPortalClientOwnerGet(externalProps.client.clientId))
        .then((res) => {
          setClient(prevClient => ({ ...prevClient, owners: res.payload }));
        })
        .catch(error => console.error("Failed to fetch owner", error));
    }
  }, [dispatch, isEditMode, externalProps.client.clientId]);

  useEffect(() => {
    const checkUserType = async () => {
      try {
        const userEmail = externalProps.userEmail || '';

        // Check if user is an owner
        const isOwnedAccount = client.owners && client.owners.some(owner =>
          owner.email && owner.email.toLowerCase() === userEmail.toLowerCase()
        );

        const adminResponse = await dispatch(clientAdminRequestGet());
        const isAdmin = adminResponse && adminResponse.payload && adminResponse.payload.admin;

        const isInternalDev = userEmail.includes('@jedix.com') || userEmail.includes('@goto.com');
        if (isOwnedAccount && isAdmin) {
          setUserType({
            isOwnedAccount: true,
            isAdmin: true,
            isInternalDev: false,
            isExternalDev: false
          });
        } else if(!isInternalDev && isOwnedAccount){
          setUserType({
            isOwnedAccount: true,
            isAdmin: false,
            isInternalDev: false,
            isExternalDev: true
          });
        } else if (isOwnedAccount) {
          setUserType({
            isOwnedAccount: true,
            isAdmin: false,
            isInternalDev: false,
            isExternalDev: false
          });
        } else if (isAdmin) {
          setUserType({
            isOwnedAccount: false,
            isAdmin: true,
            isInternalDev: false,
            isExternalDev: false
          });
        } else if (isInternalDev) {
          setUserType({
            isOwnedAccount: false,
            isAdmin: false,
            isInternalDev: true,
            isExternalDev: false
          });
        } else {
          setUserType({
            isOwnedAccount: false,
            isAdmin: false,
            isInternalDev: false,
            isExternalDev: true
          });
        }
      } catch (error) {
        console.error('Failed to check user type:', error);
        setUserType({
          isOwnedAccount: false,
          isAdmin: false,
          isInternalDev: false,
          isExternalDev: true
        });
      }
    };

    checkUserType();
  }, [dispatch, externalProps.userEmail, client?.owners]);

  useEffect(() => {
    const anySection = sectionRefs.current[sectionKeys[0]].current;
    if (!anySection) return;
    const scrollParent = findScrollParent(anySection, 'core-view__mainDisplay___');
    if (!scrollParent) return;


    function handleScroll() {
      let current = sectionKeys[0];
      for (const key of sectionKeys) {
        const sectionEl = sectionRefs.current[key].current;
        if (!sectionEl) continue;
        const sectionRect = sectionEl.getBoundingClientRect();
        const parentRect = scrollParent.getBoundingClientRect();
        if (sectionRect.top - parentRect.top - HEADER_HEIGHT <= 0) {
          current = key;
        }
      }
      setActiveSection(current);
    }

    scrollParent.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => scrollParent.removeEventListener("scroll", handleScroll);
  }, []);

  const createOriginalState = () => {
    if (!origState.current) {
      origState.current = {
        client: cloneDeep(client),
        errors: cloneDeep(errors),
        isEditMode,
        addRedirectURI,
        detailsContentsClass,
        permissionsContentsClass,
        credentialsContentsClass,
        detailsButtonClass,
        permissionsButtonClass,
        credentialsButtonClass,
        permissionsList: cloneDeep(permissionsList),
        existingPermissionsNotControlledByDevPortal,
        selectedPermissionsControlledByDevPortal,
        secretCopied,

      };
    }
  };
  //temporary function to set original state after client update
  const setOriginalState = (updatedClient) => {
    origState.current = {
      client: cloneDeep(updatedClient),
      errors: cloneDeep(errors),
      isEditMode,
      addRedirectURI,
      detailsContentsClass,
      permissionsContentsClass,
      credentialsContentsClass,
      detailsButtonClass,
      permissionsButtonClass,
      credentialsButtonClass,
      permissionsList: cloneDeep(permissionsList),
      existingPermissionsNotControlledByDevPortal,
      selectedPermissionsControlledByDevPortal,
      secretCopied,
    };
  };

  const onDetailsEditClick = () => {
    setOpenDetailsEditModel(true);
  };

  const onPermissionsEditClick = () => {
    setPermissionsEditModel(true);
  };

  const onOwnershipEditClick = () => {
    setOpenOwnershipEditModel(true);
  };

  const onRolesEditClick = () => {
    setOpenRolesEditModel(true);
  };

  const onImplicitScopesEditClick = () => {
    setOpenImplicitScopesEditModel(true);
  };

  const onRequiredScopesEditClick = () => {
    setOpenRequiredScopesEditModel(true);
  };

  const onScopesEditClick = () => {
    setOpenScopesEditModel(true);
  };

  const closeEditModel = () => {
    setOpenDetailsEditModel(false);
    setPermissionsEditModel(false);
    setOpenOwnershipEditModel(false);
    setOpenRolesEditModel(false);
    setOpenImplicitScopesEditModel(false);
    setOpenRequiredScopesEditModel(false);
    setOpenScopesEditModel(false);
  };

  const scrollToSection = (key) => {

    const sectionEl = sectionRefs.current[key].current;
    if (!sectionEl) return;
    setActiveSection(key);
    const scrollParent = findScrollParent(sectionEl, 'core-view__mainDisplay___');
    if (!scrollParent) return;
    const parentRect = scrollParent.getBoundingClientRect();
    const sectionRect = sectionEl.getBoundingClientRect();
    const scrollOffset = sectionRect.top - parentRect.top + scrollParent.scrollTop - HEADER_HEIGHT;
    scrollParent.scrollTo({ top: scrollOffset, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!sideNavRef.current) return;
    // Partial className to search for
    const partialClass = 'core-view__mainDisplay___';
    const scrollParent = findScrollParent(sideNavRef.current, partialClass);

    if (!scrollParent) {
      console.warn('Scroll container not found!');
      return;
    }

    const handleScroll = () => {
      if (scrollParent.scrollTop >= 65) {
        setSideNavTop(51);
      } else {
        setSideNavTop(200);
      }
    };

    scrollParent.addEventListener('scroll', handleScroll);
    return () => {
      scrollParent.removeEventListener('scroll', handleScroll);
    };
  }, [sideNavRef.current]);

  const setPermissions = (scopeList = []) => {
    const permissions = {};
    let clientScopes = client && client.scopes && client.scopes.length ? [...client.scopes] : [];
    if (clientScopes.indexOf(appconfig.defaultProfileScope) < 0) {
      clientScopes.unshift(appconfig.defaultProfileScope);
    }

    const permissionsControlledByDevPortal = [];
    for (let [key, groupId] of Object.entries(appconfig.newPermissionList)) {
      let scopeListWithLocale = scopeList?.filter(
        scope =>
          scope.groupId === groupId &&
          Object.keys(scope?.description ?? {}).length === 9 &&
          Object.values(scope?.description ?? {}).every(
            val => typeof val === "string" && val.trim() !== ""
          ) &&
          !!scope?.active
      );
      permissionsControlledByDevPortal.push(...scopeListWithLocale);
      const selectedScopes = scopeListWithLocale.filter(el => clientScopes.includes(el.id));
      permissions[key] = {
        name: key,
        scopes: scopeListWithLocale,
        isGranularView: false,
        hasChildScopes: scopeListWithLocale.length > 1,
        selectedScopes: selectedScopes,
        intermediate: (selectedScopes.length > 1) && (selectedScopes.length < scopeListWithLocale.length),
      };
      if (key.toLowerCase() === 'profile') {
        if (scopeListWithLocale.indexOf(appconfig.defaultProfileScope) < 0) {
          permissions[key].scopes.unshift(appconfig.defaultProfileScope);
        }
        permissions[key].isGranularView = true;
      }
    }
    // identify the selected scopes by dev portal
    const selectedPermissionsControlledByDevPortalValue = getSelectedPermisisonsControlledByDevPortal(permissions);
    // identify and store all permissions (scopes) not controlled by dev portal to include in submission
    const filteredExistingPermissionsNotControlledByDevPortal = clientScopes.filter(el => !permissionsControlledByDevPortal.includes(el)) || [];
    const existingPermissionsNotControlledByDevPortalVal = filteredExistingPermissionsNotControlledByDevPortal.slice()
      .filter(val => !selectedPermissionsControlledByDevPortalValue.includes(val));
    const selectedPermissionsControlledByDevPortalVal = selectedPermissionsControlledByDevPortalValue.slice();
    setPermissionsList(permissions);
    setExistingPermissionsNotControlledByDevPortal(existingPermissionsNotControlledByDevPortalVal);
    setSelectedPermissionsControlledByDevPortal(selectedPermissionsControlledByDevPortalVal);
    setPermissionsLoaded(true);
    return true;
  };

  useEffect(() => {
    dispatch(oauthPortalScopesGet()).then((res) => {
      setPermissions(res?.payload);
      const rawScopeList = res?.payload?.map(scope => scope.id);
      setRawPermissionList(rawScopeList);
      setFullScopesList(res?.payload || []);
    });

    dispatch(roleManagementRolesRequestGet()).then(
      (res) => {
      setFullRolesList(res?.payload || []);
      }, 
      (ex) => {
        if(ex.status === 403)
            setFullRolesList([]);
      });

    // window.scrollTo(0, 0);
    // setTimeout(() => { $('#name-input').focus(); }, 250);
    setPreviousUrlPath(location.state && location.state.prevPath);
    // redirect to the client list if the clientId is defined in the URL, but the client was not found
    //     - e.g., the page was reloaded by the client
    if (typeof externalProps.clientId !== 'undefined' && (!externalProps.client || !externalProps.client.hasOwnProperty('clientId'))) {
      return navToClientList();
    }


  }, []);

  //on change of permissions set orignal state
  useEffect(() => {
    //avoid calling createOriginalState on first render
    if (storeOrigStateNow.current) {
      createOriginalState();
    }
    else {
      storeOrigStateNow.current = true;
    }
    updateChipPermission();
  }, [permissionsList, existingPermissionsNotControlledByDevPortal, selectedPermissionsControlledByDevPortal, client]);


  const updateChipPermission = () => {
    const clientData = getClientData(client, permissionsList);
    clientData.clientStartDate = getClientStartDate(clientData.created);
    setChipPermissions(clientData);
  };

  const contentsHaveChanged = () => {
    if (origState && origState.current &&
      origState.current.client && client &&
      origState.current.selectedPermissionsControlledByDevPortal && selectedPermissionsControlledByDevPortal) {
        return !isEqual(origState.current.client, client) ||
        !isEqual([...origState.current.selectedPermissionsControlledByDevPortal].sort(), [...selectedPermissionsControlledByDevPortal].sort()) ||
        hasPendingOwnerAddition ||
        hasPendingOwnerDeletion ||
        isRedirectUriValid(addRedirectURI.trim());
    }
    return false;
  };

  const validateUserInput = async (field, value, onBlurCheck) => {
    let errorText = '';
    const foundErrors = { ...errors };

    if (foundErrors[field]) {
      delete foundErrors[field];
    }

    if (field === 'description') {
      setDisableSubmit(value.trim() === origState?.current?.client?.description ||
        (!origState?.current?.client?.description && value.trim().length === 0));
    }
    if (field === 'name') {
      setDisableSubmit(value.trim() === origState?.current?.client?.name);
      if (value.length > 128) {
        errorText = 'client-edit.validation.name-exceeds-max-length';
      }
      if (onBlurCheck && isEditMode && !value.length) {
        errorText = 'client-edit.validation.name-required';
      }
    }
    if (field === 'description' && value.length > 500) {
      errorText = 'client-edit.validation.description-exceeds-max-length';
    }

    if (field === 'theme' && value.length > 100) {
      errorText = 'client-edit.validation.theme-exceeds-max-length';
    }

    if (field === 'tenant' && value.length > 100) {
      errorText = 'client-edit.validation.tenant-exceeds-max-length';
    }


    if (field === 'addRedirectURI' && (value.length > 250 || (onBlurCheck && value && !isValidURI(value)))) {
      errorText = 'client-edit.addRedirectUrl-malformed-uri';
    }

    if (field === 'addOwnerEmail') {
      if (value.length > 250) {
        errorText = 'client-edit.validation.email-exceeds-max-length';
      } else if (onBlurCheck && value && !isValidEmail(value)) {
        errorText = 'client-edit.validation.invalid-email';
      }
    }

    if (['scopesInput', 'implicitScopesInput', 'requiredScopesInput'].includes(field)) {
      if (value && value.trim()) {
        const isValid = await scopeIsValid(value.trim());
        if (!isValid) {
          errorText = 'client-edit.validation.invalid-scope-id';
        }
      }
    }

    if (field === 'rolesInput') {
      if (value && value.trim()) {
        const foundRole = fullRolesList.find(role => role.id === value.trim());
        if (!foundRole || !foundRole.active) {
          errorText = 'client-edit.validation.invalid-role-id';
        }
      }
    }

    if (!errorText.length && field === 'addRedirectURI' && hasIllegalCharacters(value, true)) {
      errorText = 'shared.illegal-URI-character-entry';
    }

    if (!errorText.length && !['addRedirectURI', 'addOwnerEmail', 'scopesInput', 'implicitScopesInput', 'requiredScopesInput', 'rolesInput'].includes(field) && hasIllegalCharacters(value)) {
      errorText = 'shared.illegal-character-entry';
    }

    if (errorText.length) {
      foundErrors[field] = errorText;
    }

    setErrors(foundErrors);

    return !errorText.length;
  };

  const scopeIsValid = async (scope) => {
    try {
      const response = await dispatch(oauthPortalScopeIsValid(scope));
      return response?.payload?.active === true;
    } catch (error) {
      return false;
    }
  };

  const validateEntryOnBlur = async (e) => {
    const foundClient = { ...client };
    const field = e.target.id.split('-')[0];
    let value = e.target.value.trim();

    if (await validateUserInput(field, value, true)) {
      setClient(foundClient);
    }
    return true;
  };

  const enableSubmit = () => {
    return (
      Object.keys(errors || {}).length === 0 &&
      contentsHaveChanged() &&
      !externalProps.isLoading
    );
  };

  const isRedirectUriValid = () => {
    return (
      Object.keys(errors || {}).length === 0 &&
      addRedirectURI?.trim().length > 8
    );
  };

  const updateClientState = async (e) => {
    const foundClient = { ...client };
    const field = e.target.id.split('-')[0];
    let value = e.target.type === 'number' ? Number(e.target.value) : e.target.value;
    foundClient[field] = value;

    if (await validateUserInput(field, value, false)) {
      setClient(foundClient);
    }
    return true;
  };

  const onOwnerEmailChange = async (e) => {
    if (!hasPendingOwnerAddition) {
      const value = e.target.value;
      setAddOwnerEmail(value);
      await validateUserInput('addOwnerEmail', value, false);
    }
    return true;
  };

  const onOwnerEmailBlur = async (e) => {
    const value = e.target.value;
    await validateUserInput('addOwnerEmail', value, true);
    return true;
  };

  const clearScopeError = (scopeType) => {
    const foundErrors = { ...errors };
    const errorKey = `${scopeType}Input`;
    if (foundErrors[errorKey]) {
      delete foundErrors[errorKey];
      setErrors(foundErrors);
    }
  };

  const updateRedirectUriInState = async (e) => {
    const field = e.target.id.split('-')[0];
    const value = e.target.value;
    const addRedirectURIValue = value;
    if (await validateUserInput(field, value, false)) {
      setAddRedirectURI(addRedirectURIValue);
      enableSubmit();
      setDisableSubmit(false);
    }
    else {
      setDisableSubmit(true);
    }
    return true;
  };

  const handleGrantTypeChange = (grantType) => (e) => {
    const isChecked = e.target && e.target.checked;
    const foundClient = { ...client };

    if (!foundClient.grants) {
      foundClient.grants = [];
    }

    const currentGrants = [...foundClient.grants];
    const grantIndex = currentGrants.indexOf(grantType);

    if (isChecked && grantIndex === -1) {
      currentGrants.push(grantType);
    } else if (!isChecked && grantIndex > -1) {
      currentGrants.splice(grantIndex, 1);
    }

    foundClient.grants = currentGrants;
    setClient(foundClient);
    return true;
  };

  const displayToastrMessage = (toastrType, toastrMessageId, toastrParams = null) => {
    dispatch(uiShowToastrMessage(toastrType, toastrMessageId, toastrParams));
  };

  const handleXHRResult = (success, messageId, toastrParams = null) => {
    const toastrType = success ? 'success' : 'error';
    displayToastrMessage(toastrType, messageId, toastrParams);
  };

  const getSelectedPermisisonsControlledByDevPortal = (permissionsList) => {
    const selectedPermissionsControlledByDevPortalVal = [];
    for (let [key] of Object.entries(permissionsList)) {
      const selectedScopes = permissionsList[key].selectedScopes || [];
      if (selectedScopes.length) {
        selectedPermissionsControlledByDevPortalVal.push(
          ...selectedScopes.filter(scope => scope !== undefined)
        );
      }
    }

    const filterSelectedPermissionVal = selectedPermissionsControlledByDevPortalVal
      .map((val) => typeof (val) === "string" ? val : val?.id)
      .filter(scope => !!scope === true);

    if (filterSelectedPermissionVal.indexOf(appconfig.defaultProfileScope) < 0)
      filterSelectedPermissionVal.unshift(appconfig.defaultProfileScope);

    return filterSelectedPermissionVal.slice();
  };

  const getClientStartDate = () => {
    if (client && client.created) {
      const lang = externalProps.meLocale ? externalProps.meLocale.split('_')[0] : 'en';
      return moment(client.created).locale(lang).format('LL');
    }
    return '';
  };

  const permissionCheckboxClick = (name, scope) => (e) => {
    const isChecked = e.target && e.target.checked;
    const permissionsListVal = { ...permissionsList };

    if (permissionsListVal && permissionsListVal[name]) {
      const selectedScopesArr = permissionsListVal[name].selectedScopes;
      const indexOfScope = selectedScopesArr.findIndex(s => s.id === scope.id);

      if (isChecked && indexOfScope < 0) {
        selectedScopesArr.push(scope);
      } else if (!isChecked && indexOfScope > -1) {
        selectedScopesArr.splice(indexOfScope, 1);
        permissionsListVal[name].childUnchecked = true;
      }
    }
    const selectedPermissionsControlledByDevPortalVal = getSelectedPermisisonsControlledByDevPortal(permissionsListVal);
    setPermissionsList(permissionsListVal);
    setSelectedPermissionsControlledByDevPortal(selectedPermissionsControlledByDevPortalVal);
    return true;
  };

  const permissionGroupCheckboxClick = (name) => (e) => {
    const isChecked = e.target && e.target.checked;
    const permissionsListVal = { ...permissionsList };
    if (permissionsListVal && permissionsListVal[name]) {
      if (isChecked) {
        permissionsListVal[name].selectedScopes = [...permissionsListVal[name].scopes];
      }
      else if (!isChecked && permissionsListVal[name].childUnchecked) {
        permissionsListVal[name].selectedScopes = [...permissionsListVal[name].selectedScopes];
        permissionsListVal[name].childUnchecked = false;
      } else {
        permissionsListVal[name].selectedScopes = [];
      }
    }
    const selectedPermissionsControlledByDevPortalVal = getSelectedPermisisonsControlledByDevPortal(permissionsListVal);
    setPermissionsList(permissionsListVal);
    setSelectedPermissionsControlledByDevPortal(selectedPermissionsControlledByDevPortalVal);
    return true;
  };

  const togglePermissionsDetail = (name) => (e) => {
    stopEvent(e);
    const permissionsListVal = { ...permissionsList };
    if (permissionsListVal && permissionsListVal[name]) {
      permissionsListVal[name].isGranularView = !permissionsListVal[name].isGranularView;
    }
    setPermissionsList(permissionsListVal);
    return false;
  };

  const navToClientList = () => {
    if (contentsHaveChanged()) {
      dispatch(uiShowConfirmDialog("hasEdits", () => true, toClientList));
    } else {
      toClientList();
    }
  };

  const toClientList = () => {
    history("/clients");
  };

  const navToCredentialsTab = () => {
    setDetailsContentsClass('hidden');
    setPermissionsContentsClass('hidden');
    setCredentialsContentsClass('');
    setDetailsButtonClass('completed');
    setPermissionsButtonClass('completed');
    setCredentialsButtonClass('completed');
  };

  const onCancel = (e) => {
    stopEvent(e);
    setClient(origState.current && origState.current.client);
    setEditMode(origState.current && origState.current.isEditMode);
    setErrors(origState.current && origState.current.errors);
    setAddRedirectURI(origState.current && origState.current.addRedirectURI);
    setDetailsContentsClass(origState.current && origState.current.detailsContentsClass);
    setPermissionsContentsClass(origState.current && origState.current.permissionsContentsClass);
    setCredentialsContentsClass(origState.current && origState.current.credentialsContentsClass);
    setDetailsButtonClass(origState.current && origState.current.detailsButtonClass);
    setPermissionsButtonClass(origState.current && origState.current.permissionsButtonClass);
    setCredentialsButtonClass(origState.current && origState.current.credentialsButtonClass);
    setPermissionsList(origState.current && origState.current.permissionsList);
    setExistingPermissionsNotControlledByDevPortal(origState.current && origState.current.existingPermissionsNotControlledByDevPortal);
    setSelectedPermissionsControlledByDevPortal(origState.current && origState.current.selectedPermissionsControlledByDevPortal);
    setSecretCopied(origState.current && origState.current.secretCopied);
    setOwnerToDelete(null);
    setHasPendingOwnerAddition(false);
    setHasPendingOwnerDeletion(false);
    setOwnerToDelete(null);
    setAddOwnerEmail('');
    origState.current = null;
    closeEditModel();
  };

  const onAddUriClick = (e) => {
    stopEvent(e);
    const clientVal = { ...client };
    const addRedirectURIVal = addRedirectURI.trim();
    if (Object.keys(errors).length === 0 && addRedirectURIVal) {
      if (!clientVal.redirectUris) {
        clientVal.redirectUris = [];
      }
      if (clientVal.redirectUris.indexOf(addRedirectURIVal) > -1) {
        const errorsVal = { ...errors };
        errorsVal.addRedirectURI = 'client-edit.addRedirectUrl-matching-uri';
        setErrors(errorsVal);
        return false;
      }
      clientVal.redirectUris.push(addRedirectURIVal);
      setClient(clientVal);
      setAddRedirectURI('');
      setDisableSubmit(false);
      return true;
    }
    return false;
  };

  const onDeleteUriClick = (index) => (e) => {
    stopEvent(e);
    const clientVal = { ...client };
    clientVal.redirectUris.splice(index, 1);
    setDisableSubmit(false);
    setClient(clientVal);
    return true;
  };

  const isOwnerEmailValid = () => {
    return (
      Object.keys(errors || {}).filter(key => key === 'addOwnerEmail').length === 0 &&
      addOwnerEmail?.trim().length > 0 &&
      isValidEmail(addOwnerEmail.trim()) &&
      !hasPendingOwnerAddition
    );
  };

  const onAddOwner = (e) => {
    stopEvent(e);
    const clientVal = { ...client };
    const ownerEmailVal = addOwnerEmail.trim();

    if (Object.keys(errors).filter(key => key === 'addOwnerEmail').length === 0 && ownerEmailVal && !hasPendingOwnerAddition) {
      if (!clientVal.owners) {
        clientVal.owners = [...(externalProps.owners || [])];
      }

      // Check for duplicates
      const existingOwner = clientVal.owners.find(owner => owner.email.toLowerCase() === ownerEmailVal.toLowerCase());
      if (existingOwner) {
        const errorsVal = { ...errors };
        errorsVal.addOwnerEmail = 'client-edit.validation.duplicate-owner-email';
        setErrors(errorsVal);
        return false;
      }

      clientVal.owners.push({ email: ownerEmailVal });
      setClient(clientVal);
      setAddOwnerEmail('');
      setHasPendingOwnerAddition(true);

      if (origState.current && !isEqual(clientVal.owners, origState.current.client.owners)) {
        setDisableSubmit(false);
      }

      return true;
    }
    return false;
  };

  const onDeleteOwner = (index) => (e) => {
    stopEvent(e);

    if (!hasPendingOwnerDeletion && client.owners && client.owners.length > index) {
      const clientVal = { ...client };
      const ownerToRemove = clientVal.owners[index];

      clientVal.owners.splice(index, 1);
      
      setClient(clientVal);
      if (origState.current && isEqual(clientVal.owners, origState.current.client.owners)) {
        setDisableSubmit(true);
        setHasPendingOwnerAddition(false);
      }
      else {
        // Store the deleted owner for API call
        setOwnerToDelete(ownerToRemove);
        setHasPendingOwnerDeletion(true);
        setDisableSubmit(false);
      }
    }
    return true;
  };

  const validateURIEntryFromNextButton = async () => {
    const clientVal = { ...client };
    const field = "addRedirectURI";
    const value = addRedirectURI.trim();
    clientVal[field] = value;
    if (await validateUserInput(field, value, true)) {
      setClient(clientVal);
      return true;
    }
    return false;
  };

  const onTabClick = (e) => {
    const tabClicked = e.target.id.split('-')[0];
    if ((tabClicked === 'permissionsTabBtn' && addRedirectURI) &&
      !(onAddUriClick(e) && validateURIEntryFromNextButton() && isRedirectUriValid())) {
      return false;
    }
    const stateUpdate = {
      detailsContentsClass: 'hidden',
      permissionsContentsClass: 'hidden',
      credentialsContentsClass: 'hidden',
      detailsButtonClass: '',
      permissionsButtonClass: '',
      credentialsButtonClass: ''
    };
    switch (tabClicked) {
      case 'detailsTabBtn':
        stateUpdate.detailsContentsClass = '';
        stateUpdate.detailsButtonClass = 'active';
        break;
      case 'permissionsTabBtn':
        stateUpdate.permissionsContentsClass = '';
        stateUpdate.permissionsButtonClass = 'active';
        if (!isEditMode) {
          stateUpdate.detailsButtonClass = 'completed';
        }
        break;
      case 'credentialsTabBtn':
        stateUpdate.credentialsContentsClass = '';
        stateUpdate.credentialsButtonClass = 'active';
        if (!isEditMode) {
          stateUpdate.detailsButtonClass = 'completed';
          stateUpdate.permissionsButtonClass = 'completed';
          stateUpdate.credentialsButtonClass = 'completed';
        }
        break;
    }
    setDetailsContentsClass(stateUpdate.detailsContentsClass);
    setPermissionsContentsClass(stateUpdate.permissionsContentsClass);
    setCredentialsContentsClass(stateUpdate.credentialsContentsClass);
    setDetailsButtonClass(stateUpdate.detailsButtonClass);
    setPermissionsButtonClass(stateUpdate.permissionsButtonClass);
    setCredentialsButtonClass(stateUpdate.credentialsButtonClass);

    return true;
  };

  const onCopy = (str, isClientBtn) => (e) => {
    stopEvent(e);
    if (copyTextToClipboard(str) && isClientBtn) {
      displayToastrMessage('success', 'copy-to-clipboard.client-id-copied');
    }
    else if (copyTextToClipboard(str) && !isClientBtn) {
      displayToastrMessage('success', 'copy-to-clipboard.secret-copied');
    }
    return false;
  };

  const onDelete = (e) => {
    stopEvent(e);
    dispatch(uiShowConfirmDialog("deleteClient", doDelete, () => true));
  };

  const onDisable = (e) => {
    stopEvent(e);
    if(client.disabled) {
      doDisable();
    }
    else dispatch(uiShowConfirmDialog("disableClient", doDisable, () => true));
  };
  const onConfirmSecretCopy = (e) => {
    const isChecked = e.target && e.target.checked;
    setSecretCopied(isChecked);
    return true;
  };

  const doDisable = (e) => {
    return dispatch(portalClientRequestPatch(externalProps.clientId, { disabled: !client.disabled }))
        .then(
          (response) => {
            handleXHRResult(true, 'client-edit.client-update-successful');
            const updatedClient = { ...client, disabled: !client.disabled };
            setOriginalState(updatedClient);
            setClient(prevClient => ({ ...prevClient, ...response.payload }));
          },
          (error) => {
            const errorObj = getAPIError(error);
            const errorCode = errorObj && errorObj.length === 1 ? errorObj[0].code : '';
            handleXHRResult(false, 'client-edit.client-update-failed');
          },
        );

  };

  const doDelete = (e) => {
    stopEvent(e);
    return dispatch(portalClientRequestDelete(externalProps.clientId))
      .then(
        () => {
          handleXHRResult(true, 'client-edit.client-delete-successful');
          origState.current = null;
          toClientList();
        },
        () => {
          handleXHRResult(false, 'client-edit.client-delete-failed');
        },
      );
  };

  const onSave = (e) => {
    stopEvent(e);
    const dataToSave = {};
    if (client.name !== origState?.current?.client?.name) {
      dataToSave.name = client.name;
    }
    if (client.description !== origState.current?.client?.description) {
      dataToSave.description = client.description ? client.description : null;
    }
    if (!isEqual(client.redirectUris, origState.current?.client?.redirectUris)) {
      dataToSave.redirectUris = client.redirectUris;
    }
    if (isRedirectUriValid(addRedirectURI.trim())) {
      dataToSave.redirectUris = [...client.redirectUris, addRedirectURI.trim()];
    }
    if (!isEqual(client.grants, origState.current?.client?.grants)) {
      dataToSave.grants = client.grants || [];
    }
    if (client.autoApproved !== origState.current?.client?.autoApproved) {
      dataToSave.autoApproved = client.autoApproved;
    }
    if (client.confidential !== origState.current?.client?.confidential) {
      dataToSave.confidential = client.confidential;
    }
    if (client.scopeValidation !== origState.current?.client?.scopeValidation) {
      dataToSave.scopeValidation = client.scopeValidation;
    }
    if (client.disabled !== origState.current?.client?.disabled) {
      dataToSave.disabled = client.disabled;
    }
    if (client.theme !== origState.current?.client?.theme) {
      dataToSave.theme = client.theme === '' ? null : client.theme;
    }
    if (client.tenant !== origState.current?.client?.tenant) {
      dataToSave.tenant = client.tenant === '' ? null : client.tenant;
    }
    if (client.accessTokenValidity !== origState.current?.client?.accessTokenValidity) {
      dataToSave.accessTokenValidity = client.accessTokenValidity;
    }
    if (client.refreshTokenValidity !== origState.current?.client?.refreshTokenValidity) {
      dataToSave.refreshTokenValidity = client.refreshTokenValidity;
    }
    if (!isEqual(client.roles, origState.current?.client?.roles)) {
      dataToSave.roles = client.roles || [];
    }

    if (!isEqual(client.implicitScopes, origState.current?.client?.implicitScopes)) {
      dataToSave.implicitScopes = client.implicitScopes || [];
    }
    if (!isEqual(client.requiredScopes, origState.current?.client?.requiredScopes)) {
      dataToSave.requiredScopes = client.requiredScopes || [];
    }
    
    if (!isEqual(origState.current?.selectedPermissionsControlledByDevPortal, selectedPermissionsControlledByDevPortal)) {
      dataToSave.scopes = [...selectedPermissionsControlledByDevPortal, ...existingPermissionsNotControlledByDevPortal];
      const identityReadScopeIndex = dataToSave.scopes.indexOf(appconfig.defaultProfileScope);
      if (identityReadScopeIndex > -1) {
        dataToSave.scopes.splice(identityReadScopeIndex, 1);
      }
    }

    if (!isEqual(client.scopes, origState.current?.client?.scopes)) {
      const scopes = [...(client.scopes || [])];
      if (scopes.indexOf(appconfig.defaultProfileScope) < 0) {
        scopes.push(appconfig.defaultProfileScope);
      }

      dataToSave.scopes = scopes;
    }

    // if (!!dataToSave?.scopes?.length) {
    //   dataToSave.scopes = dataToSave.scopes
    //     .filter(scope => !!scope && scope !== appconfig.defaultProfileScope)
    //     .map(scope => typeof scope === "string" ? scope : scope?.id);
    // }
    

    if (hasPendingOwnerAddition) {
      // Get the newly added owner (last one in the array)
      const addedOwners = client.owners.filter(owner =>
        !origState.current?.client?.owners?.some(origOwner => origOwner.email === owner.email)
      );
      if (addedOwners.length > 0) {
        let ownerToAdd = addedOwners[addedOwners.length - 1]?.email;
        dispatch(oauthPortalClientOwnerAdd(externalProps.clientId, ownerToAdd)).then(
        () => {
          handleXHRResult(true, 'client-edit.client-update-successful');
          setHasPendingOwnerAddition(false);
          history("/clients");
          removeHandledErrorCodes(getErrorCodesArray());
        }, (error) => {
          removeHandledErrorCodes(getErrorCodesArray());
          handleXHRResult(false, 'client-edit.client-update-failed');
          setHasPendingOwnerAddition(false);
          return;
        });
      }
    }

    // Handle pending owner deletion
    if (hasPendingOwnerDeletion && ownerToDelete) {
      let email = ownerToDelete?.email;
      dispatch(oauthPortalClientOwnerDelete(externalProps.clientId, email)).then(() => handleXHRResult(true, 'client-edit.client-update-successful'));
    }

    // Save the data:
    if (isEditMode) {
      closeEditModel();
      return dispatch(portalClientRequestPatch(externalProps.clientId, dataToSave))
        .then(
          () => {
            removeHandledErrorCodes(getErrorCodesArray());
            handleXHRResult(true, 'client-edit.client-update-successful');
            setHasPendingOwnerAddition(false);
            setHasPendingOwnerDeletion(false);
            setOwnerToDelete(null);
            origState.current = null;
            history("/clients");
          },
          (error) => {
            removeHandledErrorCodes(getErrorCodesArray());
            const errorObj = getAPIError(error);
            const errorCode = errorObj && errorObj.length === 1 ? errorObj[0].code : '';
            if (errorCode === 'duplicate.name') {
              handleXHRResult(false, 'client-edit.client-update-failed-dup-name');
            } else {
              handleXHRResult(false, 'client-edit.client-update-failed');
            }
          },
        );

    } else {
      // Add the default scope if none selected
      // if(!dataToSave?.scopes?.length) {
      //   dataToSave.scopes = [appconfig.defaultProfileScope];
      // }
      // Add the default grant values
      dataToSave.grants = ["authorization_code", "implicit", "refresh_token"];
      setHandledErrorCodes(getErrorCodesArray());

      return dispatch(portalClientRequestPost(dataToSave))
        .then(
          () => {
            removeHandledErrorCodes(getErrorCodesArray());
            handleXHRResult(true, 'client-edit.client-add-successful');
            setHasPendingOwnerAddition(false);
            setHasPendingOwnerDeletion(false);
            setOwnerToDelete(null);
            navToCredentialsTab();
          },
          (error) => {
            removeHandledErrorCodes(getErrorCodesArray());
            const errorObj = getAPIError(error);
            const errorCode = errorObj && errorObj.length === 1 ? errorObj[0].code : '';
            if (errorCode === 'duplicate.name') {
              handleXHRResult(false, 'client-edit.client-add-failed-dup-name');
            } else {
              handleXHRResult(false, 'client-edit.client-add-failed');
            }
          },
        );
    }
  };

  return (
    <ClientEdit
      isEditMode={isEditMode}
      tabClick={onTabClick}
      client={client}
      errors={errors}
      onChange={updateClientState}
      onRedirectUriChange={updateRedirectUriInState}
      onBlur={validateEntryOnBlur}
      onSave={onSave}
      onDisable={onDisable}
      onDelete={onDelete}
      onDoneClick={toClientList}
      isDone={secretCopied}
      onConfirmSecretCopy={onConfirmSecretCopy}
      detailsContentsClass={detailsContentsClass}
      permissionsContentsClass={permissionsContentsClass}
      credentialsContentsClass={credentialsContentsClass}
      detailsButtonClass={detailsButtonClass}
      permissionsButtonClass={permissionsButtonClass}
      credentialsButtonClass={credentialsButtonClass}
      permissionsList={permissionsList}
      togglePermissionsDetail={togglePermissionsDetail}
      enableCTAs={enableSubmit()}
      canCancel={contentsHaveChanged()}
      addRedirectURI={addRedirectURI}
      isRedirectUriValid={isRedirectUriValid()}
      onAddUriClick={onAddUriClick}
      onDeleteUriClick={onDeleteUriClick}
      permissionCheckboxClick={permissionCheckboxClick}
      permissionGroupCheckboxClick={permissionGroupCheckboxClick}
      onCopy={onCopy}
      newClientCredentials={externalProps.newClientCredentials}
      isLoading={false}
      navToClientList={navToClientList}
      onDetailsEditClick={onDetailsEditClick}
      openDetailsEditModel={openDetailsEditModel}
      onPermissionsEditClick={onPermissionsEditClick}
      openPermissionsEditModel={openPermissionsEditModel}
      closeEditModel={closeEditModel}
      clientData={chipPermissions}
      onCancel={onCancel}
      canWrite={externalProps.canWrite}
      disableSubmit={disableSubmit}
      meLocale={externalProps.meLocale}
      onOwnershipEditClick={onOwnershipEditClick}
      openOwnershipEditModel={openOwnershipEditModel}
      onRolesEditClick={onRolesEditClick}
      openRolesEditModel={openRolesEditModel}
      sideNavTop={sideNavTop}
      sideNavRef={sideNavRef}
      sectionRefs={sectionRefs.current}
      onMenuClick={scrollToSection}
      activeSection={activeSection}
      onGrantTypeChange={handleGrantTypeChange}
      onAuthorizationChange={handleAuthorizationChange}
      onAddRole={handleAddRole}
      onDeleteRole={handleDeleteRole}
      onImplicitScopesEditClick={onImplicitScopesEditClick}
      openImplicitScopesEditModel={openImplicitScopesEditModel}
      onAddImplicitScope={handleAddImplicitScope}
      onDeleteImplicitScope={handleDeleteImplicitScope}
      onRequiredScopesEditClick={onRequiredScopesEditClick}
      openRequiredScopesEditModel={openRequiredScopesEditModel}
      onAddRequiredScope={handleAddRequiredScope}
      onDeleteRequiredScope={handleDeleteRequiredScope}
      onScopesEditClick={onScopesEditClick}
      openScopesEditModel={openScopesEditModel}
      onAddScope={handleAddScope}
      onDeleteScope={handleDeleteScope}
      rawScopeList={rawPermissionList}
      owners={externalProps.owners}
      addOwnerEmail={addOwnerEmail}
      onOwnerEmailChange={onOwnerEmailChange}
      onOwnerEmailBlur={onOwnerEmailBlur}
      onAddOwner={onAddOwner}
      isOwnerEmailValid={isOwnerEmailValid()}
      onDeleteOwner={onDeleteOwner}
      hasPendingOwnerAddition={hasPendingOwnerAddition}
      hasPendingOwnerDeletion={hasPendingOwnerDeletion}
      ownerToDelete={ownerToDelete}
      scopeValidationErrors={{
        scopes: errors?.scopesInput,
        implicitScopes: errors?.implicitScopesInput,
        requiredScopes: errors?.requiredScopesInput,
        roles: errors?.rolesInput
      }}
      clearScopeError={clearScopeError}
      userType={userType}
      restrictedScopes={existingPermissionsNotControlledByDevPortal}
    />
  );
};

export default ClientEditContainer;
